import type { DailyBoard, DailyWorkspace } from "./types";

const workspaceStorageKey = "dawndiver.daily.workspace";
const oldBoardStorageKey = "dawndiver.daily.board";
const oldNodesStorageKey = "dawndiver.daily.nodes";

export function loadDailyWorkspace(): DailyWorkspace {
  if (typeof window === "undefined") {
    return createDefaultWorkspace();
  }

  const storedWorkspace = window.localStorage.getItem(workspaceStorageKey);

  if (storedWorkspace) {
    try {
      const workspace = JSON.parse(storedWorkspace) as DailyWorkspace;

      if (workspace.canvases.length > 0) {
        return workspace;
      }
    } catch {
      return createDefaultWorkspace();
    }
  }

  return createDefaultWorkspace(loadLegacyBoard());
}

export function saveDailyWorkspace(workspace: DailyWorkspace) {
  window.localStorage.setItem(
    workspaceStorageKey,
    JSON.stringify({
      activeCanvasId: workspace.activeCanvasId,
      canvases: workspace.canvases.map((canvas) => ({
        ...canvas,
        board: {
          nodes: canvas.board.nodes.map((node) => ({
            ...node,
            data: {
              text: node.data.text,
            },
          })),
          edges: canvas.board.edges.map((edge) => ({
            ...edge,
            data: {},
          })),
        },
      })),
    }),
  );
}

export function createBlankCanvas(name: string) {
  return {
    id: createId(),
    name,
    board: {
      nodes: [],
      edges: [],
    },
  };
}

export function createId() {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }

  return `daily-${Date.now()}`;
}

function createDefaultWorkspace(board: DailyBoard = { nodes: [], edges: [] }) {
  const canvas = {
    id: createId(),
    name: "Today",
    board,
  };

  return {
    activeCanvasId: canvas.id,
    canvases: [canvas],
  };
}

function loadLegacyBoard(): DailyBoard {
  const storedBoard = window.localStorage.getItem(oldBoardStorageKey);

  if (storedBoard) {
    try {
      return JSON.parse(storedBoard) as DailyBoard;
    } catch {
      return { nodes: [], edges: [] };
    }
  }

  const storedNodes = window.localStorage.getItem(oldNodesStorageKey);

  if (!storedNodes) {
    return { nodes: [], edges: [] };
  }

  try {
    return {
      nodes: JSON.parse(storedNodes),
      edges: [],
    };
  } catch {
    return { nodes: [], edges: [] };
  }
}
